import { apiClient } from "../axiosInstance";

class RequestService {
  constructor() {
    this.baseUrl = "/requests";
    this.adminBaseUrl = "/requests/admin/requests";
  }

  // ==================== USER ROUTES ====================

  /**
   * Create a new request
   * @param {Object} requestData - Request data
   * @param {string} requestData.message - Request message (required)
   * @param {string} [requestData.subject] - Subject of the request
   * @param {string} [requestData.priority] - Priority (LOW, MEDIUM, HIGH, URGENT)
   * @param {string} [requestData.deliveryAddress] - Delivery address
   * @param {string} [requestData.deliveryCity] - Delivery city
   * @param {string} [requestData.deliveryCountry] - Delivery country
   * @param {string} [requestData.deliveryPostalCode] - Delivery postal code
   * @param {string} [requestData.requiredDeliveryDate] - Required delivery date (ISO 8601)
   * @param {number} [requestData.estimatedValue] - Estimated value of request
   * @param {Array} [requestData.products] - Products to include in request
   */
  async createRequest(requestData) {
    try {
      const response = await apiClient.post(this.baseUrl, requestData);
      return response.data;
    } catch (error) {
      console.error("Create request error:", error);
      throw error;
    }
  }

  /**
   * Get user's requests
   * @param {Object} params - Query parameters
   * @param {number} [params.page] - Page number
   * @param {number} [params.limit] - Items per page (max 100)
   * @param {string} [params.status] - Filter by status (PENDING, IN_REVIEW, APPROVED, IN_PROGRESS, COMPLETED, CANCELLED, REJECTED)
   */
  async getMyRequests(params = {}) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/my-requests`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Get my requests error:", error);
      throw error;
    }
  }

  /**
   * Get editable requests for user
   */
  async getEditableRequests() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/editable`);
      return response.data;
    } catch (error) {
      console.error("Get editable requests error:", error);
      throw error;
    }
  }

  /**
   * Get cancellable requests for user
   */
  async getCancellableRequests() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/cancellable`);
      return response.data;
    } catch (error) {
      console.error("Get cancellable requests error:", error);
      throw error;
    }
  }

  /**
   * Get request by ID (user - own requests only)
   * @param {number} id - Request ID
   */
  async getRequestById(id) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error("Get request by id error:", error);
      throw error;
    }
  }

  /**
   * Update request (user - own requests only)
   * @param {number} id - Request ID
   * @param {Object} updateData - Data to update
   * @param {string} [updateData.subject] - Subject of the request
   * @param {string} [updateData.message] - Request message
   * @param {string} [updateData.priority] - Priority (LOW, MEDIUM, HIGH, URGENT)
   * @param {string} [updateData.deliveryAddress] - Delivery address
   * @param {string} [updateData.deliveryCity] - Delivery city
   * @param {string} [updateData.deliveryCountry] - Delivery country
   * @param {string} [updateData.deliveryPostalCode] - Delivery postal code
   * @param {string} [updateData.requiredDeliveryDate] - Required delivery date (ISO 8601)
   */
  async updateRequest(id, updateData) {
    try {
      const response = await apiClient.patch(
        `${this.baseUrl}/${id}`,
        updateData,
      );
      return response.data;
    } catch (error) {
      console.error("Update request error:", error);
      throw error;
    }
  }

  /**
   * Cancel request (user - own requests only)
   * @param {number} id - Request ID
   * @param {string} [reason] - Reason for cancellation
   */
  async cancelRequest(id, reason = "") {
    try {
      const response = await apiClient.patch(`${this.baseUrl}/${id}/cancel`, {
        reason,
      });
      return response.data;
    } catch (error) {
      console.error("Cancel request error:", error);
      throw error;
    }
  }

  /**
   * Add product to request
   * @param {number} requestId - Request ID
   * @param {Object} productData - Product data
   * @param {number} productData.productId - Product ID (required)
   * @param {number} [productData.quantity] - Quantity (default: 1)
   * @param {string} [productData.specifications] - Product specifications
   * @param {string} [productData.notes] - Additional notes
   */
  async addProductToRequest(requestId, productData) {
    try {
      const response = await apiClient.post(
        `${this.baseUrl}/${requestId}/products`,
        productData,
      );
      return response.data;
    } catch (error) {
      console.error("Add product to request error:", error);
      throw error;
    }
  }

  /**
   * Remove product from request
   * @param {number} requestId - Request ID
   * @param {number} itemId - Request item ID
   */
  async removeProductFromRequest(requestId, itemId) {
    try {
      const response = await apiClient.delete(
        `${this.baseUrl}/${requestId}/products/${itemId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Remove product from request error:", error);
      throw error;
    }
  }

  // ==================== ADMIN ROUTES ====================

  /**
   * Get all requests with pagination and filters (Admin only)
   * @param {Object} params - Query parameters
   * @param {number} [params.page] - Page number
   * @param {number} [params.limit] - Items per page (max 100)
   * @param {string} [params.status] - Filter by status
   * @param {string} [params.priority] - Filter by priority
   * @param {number} [params.userId] - Filter by user ID
   * @param {number} [params.assignedTo] - Filter by assigned admin ID
   * @param {string} [params.search] - Search term
   * @param {string} [params.fromDate] - Start date (ISO 8601)
   * @param {string} [params.toDate] - End date (ISO 8601)
   * @param {string} [params.sortBy] - Sort field
   * @param {string} [params.sortOrder] - Sort order (ASC, DESC)
   */
  async getAllRequests(params = {}) {
    try {
      const response = await apiClient.get(this.adminBaseUrl, { params });
      return response.data;
    } catch (error) {
      console.error("Get all requests error:", error);
      throw error;
    }
  }

  /**
   * Get request statistics (Admin only)
   */
  async getRequestStats() {
    try {
      const response = await apiClient.get(`${this.adminBaseUrl}/stats`);
      return response.data;
    } catch (error) {
      console.error("Get request stats error:", error);
      throw error;
    }
  }

  /**
   * Get unassigned requests (Admin only)
   */
  async getUnassignedRequests() {
    try {
      const response = await apiClient.get(`${this.adminBaseUrl}/unassigned`);
      return response.data;
    } catch (error) {
      console.error("Get unassigned requests error:", error);
      throw error;
    }
  }

  /**
   * Get overdue requests (Admin only)
   * @param {number} days - Number of days overdue (default: 7)
   */
  async getOverdueRequests(days = 7) {
    try {
      const response = await apiClient.get(`${this.adminBaseUrl}/overdue`, {
        params: { days },
      });
      return response.data;
    } catch (error) {
      console.error("Get overdue requests error:", error);
      throw error;
    }
  }

  /**
   * Get recent requests (Admin only)
   * @param {number} days - Number of days to look back (default: 30)
   * @param {number} limit - Number of requests to return (max 100)
   */
  async getRecentRequests(days = 30, limit = 10) {
    try {
      const response = await apiClient.get(`${this.adminBaseUrl}/recent`, {
        params: { days, limit },
      });
      return response.data;
    } catch (error) {
      console.error("Get recent requests error:", error);
      throw error;
    }
  }

  /**
   * Get most requested products (Admin only)
   * @param {number} limit - Number of products to return (max 100)
   */
  async getMostRequestedProducts(limit = 10) {
    try {
      const response = await apiClient.get(
        `${this.adminBaseUrl}/most-requested-products`,
        {
          params: { limit },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Get most requested products error:", error);
      throw error;
    }
  }

  /**
   * Get requests by date range (Admin only)
   * @param {string} startDate - Start date (ISO 8601)
   * @param {string} endDate - End date (ISO 8601)
   */
  async getRequestsByDateRange(startDate, endDate) {
    try {
      const response = await apiClient.get(`${this.adminBaseUrl}/date-range`, {
        params: { startDate, endDate },
      });
      return response.data;
    } catch (error) {
      console.error("Get requests by date range error:", error);
      throw error;
    }
  }

  /**
   * Get requests by product ID (Admin only)
   * @param {number} productId - Product ID
   */
  async getRequestsByProductId(productId) {
    try {
      const response = await apiClient.get(
        `${this.adminBaseUrl}/by-product/${productId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Get requests by product id error:", error);
      throw error;
    }
  }

  /**
   * Get request by ID (Admin only)
   * @param {number} id - Request ID
   */
  async getAdminRequestById(id) {
    try {
      const response = await apiClient.get(`${this.adminBaseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error("Get admin request by id error:", error);
      throw error;
    }
  }

  /**
   * Get request by request number (Admin only)
   * @param {string} requestNumber - Request number
   */
  async getRequestByNumber(requestNumber) {
    try {
      const response = await apiClient.get(
        `${this.adminBaseUrl}/number/${requestNumber}`,
      );
      return response.data;
    } catch (error) {
      console.error("Get request by number error:", error);
      throw error;
    }
  }

  /**
   * Get request by UUID (Admin only)
   * @param {string} uuid - Request UUID
   */
  async getRequestByUuid(uuid) {
    try {
      const response = await apiClient.get(`${this.adminBaseUrl}/uuid/${uuid}`);
      return response.data;
    } catch (error) {
      console.error("Get request by uuid error:", error);
      throw error;
    }
  }

  /**
   * Update request status (Admin only)
   * @param {number} id - Request ID
   * @param {string} status - New status (PENDING, IN_REVIEW, APPROVED, IN_PROGRESS, COMPLETED, CANCELLED, REJECTED)
   * @param {string} [adminNotes] - Admin notes
   */
  async updateRequestStatus(id, status, adminNotes = "") {
    try {
      const response = await apiClient.patch(
        `${this.adminBaseUrl}/${id}/status`,
        {
          status,
          adminNotes,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Update request status error:", error);
      throw error;
    }
  }

  /**
   * Assign request to admin (Admin only)
   * @param {number} id - Request ID
   */
  async assignRequest(id) {
    try {
      const response = await apiClient.patch(
        `${this.adminBaseUrl}/${id}/assign`,
      );
      return response.data;
    } catch (error) {
      console.error("Assign request error:", error);
      throw error;
    }
  }

  /**
   * Add admin response to request (Admin only)
   * @param {number} id - Request ID
   * @param {string} adminResponse - Admin response message
   */
  async addAdminResponse(id, adminResponse) {
    try {
      const response = await apiClient.post(
        `${this.adminBaseUrl}/${id}/response`,
        {
          adminResponse,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Add admin response error:", error);
      throw error;
    }
  }

  /**
   * Delete request (Admin only)
   * @param {number} id - Request ID
   */
  async deleteRequest(id) {
    try {
      const response = await apiClient.delete(`${this.adminBaseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error("Delete request error:", error);
      throw error;
    }
  }

  /**
   * Send response email to customer (Admin only)
   * @param {number} requestId - Request ID
   * @param {string} response - Response message
   * @param {string} customerEmail - Customer email address
   */
  async sendResponseEmail(requestId, response, customerEmail) {
    try {
      const responseData = await apiClient.post(
        `${this.adminBaseUrl}/${requestId}/send-response-email`,
        {
          response,
          customerEmail,
        },
      );
      return responseData.data;
    } catch (error) {
      console.error("Send response email error:", error);
      throw error;
    }
  }

  /**
   * Bulk delete requests (Admin only)
   * @param {number[]} ids - Array of request IDs
   */
  async bulkDeleteRequests(ids) {
    try {
      const response = await apiClient.post(
        `${this.adminBaseUrl}/bulk-delete`,
        { ids },
      );
      return response.data;
    } catch (error) {
      console.error("Bulk delete requests error:", error);
      throw error;
    }
  }

  /**
   * Bulk update request status (Admin only)
   * @param {number[]} ids - Array of request IDs
   * @param {string} status - New status
   */
  async bulkUpdateStatus(ids, status) {
    try {
      const response = await apiClient.patch(
        `${this.adminBaseUrl}/bulk-status`,
        {
          ids,
          status,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Bulk update status error:", error);
      throw error;
    }
  }

  /**
   * Bulk assign requests to admin (Admin only)
   * @param {number[]} ids - Array of request IDs
   */
  async bulkAssignToAdmin(ids) {
    try {
      const response = await apiClient.patch(
        `${this.adminBaseUrl}/bulk-assign`,
        { ids },
      );
      return response.data;
    } catch (error) {
      console.error("Bulk assign to admin error:", error);
      throw error;
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Get status badge color based on request status
   * @param {string} status - Request status
   */
  getStatusBadge(status) {
    const badges = {
      PENDING: {
        color:
          "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
        icon: "Clock",
      },
      IN_REVIEW: {
        color:
          "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
        icon: "Eye",
      },
      APPROVED: {
        color:
          "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
        icon: "CheckCircle",
      },
      IN_PROGRESS: {
        color:
          "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400",
        icon: "Loader",
      },
      COMPLETED: {
        color:
          "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400",
        icon: "CheckCircle2",
      },
      CANCELLED: {
        color: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400",
        icon: "XCircle",
      },
      REJECTED: {
        color: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
        icon: "XOctagon",
      },
    };
    return badges[status] || badges.PENDING;
  }

  /**
   * Get priority badge color based on request priority
   * @param {string} priority - Request priority
   */
  getPriorityBadge(priority) {
    const badges = {
      LOW: {
        color: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400",
        icon: "Flag",
      },
      MEDIUM: {
        color:
          "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
        icon: "Flag",
      },
      HIGH: {
        color:
          "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400",
        icon: "AlertTriangle",
      },
      URGENT: {
        color: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
        icon: "AlertCircle",
      },
    };
    return badges[priority] || badges.MEDIUM;
  }

  /**
   * Get status label for display
   * @param {string} status - Request status
   */
  getStatusLabel(status) {
    const labels = {
      PENDING: "Pending",
      IN_REVIEW: "In Review",
      APPROVED: "Approved",
      IN_PROGRESS: "In Progress",
      COMPLETED: "Completed",
      CANCELLED: "Cancelled",
      REJECTED: "Rejected",
    };
    return labels[status] || status;
  }

  /**
   * Get priority label for display
   * @param {string} priority - Request priority
   */
  getPriorityLabel(priority) {
    const labels = {
      LOW: "Low",
      MEDIUM: "Medium",
      HIGH: "High",
      URGENT: "Urgent",
    };
    return labels[priority] || priority;
  }
}

// Create and export a single instance
export const requestService = new RequestService();
